import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { lazy, Suspense } from "react";

// Critical components - keep synchronous for fast initial load
import PublicLayout from "./components/public/PublicLayout";
import Home from "./pages/public/Home";

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Lazy loaded public pages
const About = lazy(() => import("./pages/public/About"));
const Contact = lazy(() => import("./pages/public/Contact"));
const Blog = lazy(() => import("./pages/public/Blog"));
const BlogPost = lazy(() => import("./pages/public/BlogPost"));
const BlogSubmit = lazy(() => import("./pages/public/BlogSubmit"));
const Videos = lazy(() => import("./pages/public/Videos"));
const PublicPortfolio = lazy(() => import("./pages/public/Projects"));
const FAQ = lazy(() => import("./pages/public/FAQ"));
const Privacy = lazy(() => import("./pages/public/Privacy"));
const Terms = lazy(() => import("./pages/public/Terms"));
const DocumentView = lazy(() => import("./pages/public/DocumentView"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy loaded dashboard pages
const Index = lazy(() => import("./pages/Index"));
const Clients = lazy(() => import("./pages/Clients"));
const ClientDetails = lazy(() => import("./pages/ClientDetails"));
const Projects = lazy(() => import("./pages/Projects"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const Finance = lazy(() => import("./pages/Finance"));
const Quotations = lazy(() => import("./pages/finance/Quotations"));
const Invoices = lazy(() => import("./pages/finance/Invoices"));
const Contracts = lazy(() => import("./pages/finance/Contracts"));
const MaintenanceContracts = lazy(() => import("./pages/finance/MaintenanceContracts"));
const Payments = lazy(() => import("./pages/finance/Payments"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const Users = lazy(() => import("./pages/Users"));
const ExpensesPage = lazy(() => import("./pages/ExpensesPage"));
const PartnershipPage = lazy(() => import("./pages/PartnershipPage"));
const RemindersPage = lazy(() => import("./pages/RemindersPage"));
const Domains = lazy(() => import("./pages/Domains"));
const HostingPage = lazy(() => import("./pages/Hosting"));
const ContactRequests = lazy(() => import("./pages/ContactRequests"));
const BlogManagement = lazy(() => import("./pages/BlogManagement"));
const BlogEditor = lazy(() => import("./pages/BlogEditor"));
const ChatbotConversations = lazy(() => import("./pages/ChatbotConversations"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Auth Pages with Public Layout */}
              <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
              <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />

              {/* Public Pages - Home is not lazy loaded for fast FCP */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
              <Route path="/blog/submit" element={<PublicLayout><BlogSubmit /></PublicLayout>} />
              <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
              <Route path="/videos" element={<PublicLayout><Videos /></PublicLayout>} />
              <Route path="/portfolio" element={<PublicLayout><PublicPortfolio /></PublicLayout>} />
              <Route path="/faq" element={<PublicLayout><FAQ /></PublicLayout>} />
              <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
              <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />

              {/* Public Document View - Without Layout (standalone page) */}
              <Route path="/doc/:type/:id" element={<DocumentView />} />

              {/* Protected Routes - All Users */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <Index />
                </ProtectedRoute>
              } />
              <Route path="/projects" element={
                <ProtectedRoute>
                  <Projects />
                </ProtectedRoute>
              } />
              <Route path="/projects/:id" element={
                <ProtectedRoute>
                  <ProjectDetails />
                </ProtectedRoute>
              } />

              {/* Protected Routes - Management Only (Admin + Partner) */}
              <Route path="/clients" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Clients />
                </ProtectedRoute>
              } />
              <Route path="/clients/:id" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <ClientDetails />
                </ProtectedRoute>
              } />
              <Route path="/domains" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Domains />
                </ProtectedRoute>
              } />
              <Route path="/hosting" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <HostingPage />
                </ProtectedRoute>
              } />
              <Route path="/contacts" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <ContactRequests />
                </ProtectedRoute>
              } />
              <Route path="/finance" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Finance />
                </ProtectedRoute>
              } />
              <Route path="/finance/quotations" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Quotations />
                </ProtectedRoute>
              } />
              <Route path="/finance/invoices" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Invoices />
                </ProtectedRoute>
              } />
              <Route path="/finance/contracts" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Contracts />
                </ProtectedRoute>
              } />
              <Route path="/finance/maintenance" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <MaintenanceContracts />
                </ProtectedRoute>
              } />
              <Route path="/finance/payments" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Payments />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <Reports />
                </ProtectedRoute>
              } />
              <Route path="/partnership" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <PartnershipPage />
                </ProtectedRoute>
              } />
              <Route path="/expenses" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <ExpensesPage />
                </ProtectedRoute>
              } />
              <Route path="/reminders" element={
                <ProtectedRoute allowedRoles={['admin', 'partner']}>
                  <RemindersPage />
                </ProtectedRoute>
              } />

              {/* Protected Routes - Admin Only */}
              <Route path="/users" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Users />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/blog-admin" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <BlogManagement />
                </ProtectedRoute>
              } />
              <Route path="/blog-admin/new" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <BlogEditor />
                </ProtectedRoute>
              } />
              <Route path="/blog-admin/edit/:id" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <BlogEditor />
                </ProtectedRoute>
              } />
              <Route path="/chatbot" element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ChatbotConversations />
                </ProtectedRoute>
              } />

              {/* Catch-all Route */}
              <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
